import TitleBar from '../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';

import { Footer } from '../../../components/common/Footer';
import { Button } from '../../../components';
import {
  CREATE_LOCATION,
  DELETE_LOCATION,
  EDIT_LOCATION,
  FETCH_LOCATIONS,
} from '../queries';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import { cleanUpObject, tableSortingMethods } from '../../../utils';
import { formFields } from './forms/formFields';
import { templateColumnConfigs } from './templateUploadColumns';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import QueryErrorBoundary from '../../../components/error-handling';
import TitleUpdater from '../../../components/common/TitleUpdater';

export type FindAllLocations = {
  capacity: number;
  capacityUnit: string;
  city: string;
  country: string;
  employeeCount: number;
  locationAddress: string;
  locationName: string;
  locationType: string;
  rebuildCost: number;
  rebuildTime: number;
  state: string;
  street: string;
  uuid: string;
  zipCode: string;
};

export interface LocationsReturned {
  findAllLocations: FindAllLocations[];
}

function Locations() {
  const { selectedTags, isLocation } = useUserContext();

  const { data, loading, error } = useQuery(FETCH_LOCATIONS);

  const filteredLocations = getFilteredTags({
    data: data?.findAllLocations || [],
    selectedTags,
    condition: isLocation,
    lookUpKey: 'uuid',
  });

  const [createLocation] = useMutation(CREATE_LOCATION, {
    refetchQueries: ['GetLocations'],
  });

  const [updateLocation] = useMutation(EDIT_LOCATION, {
    refetchQueries: ['GetLocations'],
  });

  const [deleteLocation] = useMutation(DELETE_LOCATION, {
    refetchQueries: ['GetLocations'],
  });

  const navigate = useNavigate();

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'locationName',
      key: 'locationName',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'locationType',
      key: 'locationType',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationType, b.locationType),
      render: (text: string) => (
        <span className="custom-table--column">
          {text.split(/(?=[A-Z])/).join(' ')}
        </span>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'locationAddress',
      key: 'locationAddress',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationAddress, b.locationAddress),
      render: (_, { street, city, state, zipCode, country }) => {
        const addressParts = [street, city, state, zipCode, country];

        const formattedAddress = addressParts.filter((part) => part).join(', ');
        return <span className="custom-table--column">{formattedAddress}</span>;
      },
    },
    {
      title: () => (
        <div>
          Rebuilt Time
          <br />
          (months)
        </div>
      ),
      dataIndex: 'rebuildTime',
      key: 'rebuildTime',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.rebuildTime, b.rebuildTime),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
  ];

  const extendedActions = (record: any) => (
    <Button
      type="primary-rounded"
      className="text-sm"
      onClick={() =>
        navigate(
          `/locations/${record.uuid}?locationName=${record.locationName}`,
        )
      }
    >
      Process Areas
    </Button>
  );

  const locationsFormatter = (location: any) => {
    const capacity = parseInt(location.capacity?.toString());
    const employeeCount = parseInt(location.employeeCount?.toString());
    const rebuildCost = parseFloat(location.rebuildCost?.toString() || '0');
    const rebuildTime = parseInt(location.rebuildTime?.toString());

    return cleanUpObject({
      ...location,
      capacity,
      employeeCount,
      rebuildCost,
      rebuildTime,
    });
  };

  const modifyVariables = ({ values }: ModifyVariablesArgs) => {
    const location = locationsFormatter(values);

    return location;
  };

  const templateUploadConfig = {
    duplicateKey: 'locationName',
    itemLabel: 'Location',
    sampleItemIdentifier: 'Head Office',
    formatter: locationsFormatter,
  };

  return (
    <>
      <TitleUpdater title="Locations" />
      <TitleBar
        title="Data Inputs"
        subtitle="Locations"
        brief="Adding locations to MEO will support the quantification of exposures and development of
                      mitigation strategies. The more information added for each location, the more reporting options
                      will be available on the Risk Hub."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Locations"
          columns={columns}
          data={filteredLocations}
          loading={loading}
          editable
          formFields={formFields}
          createGQL={createLocation}
          updateGQL={updateLocation}
          deleteGQL={deleteLocation}
          modifyVariables={modifyVariables}
          extendActions={extendedActions}
          templateColumns={templateColumnConfigs}
          templateUploadConfig={templateUploadConfig}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/data-inputs'} />
    </>
  );
}

export default Locations;
